// Owns the list of recorded takes for the current session. RAM only —
// blobs can be tens of MB, persisting them to localStorage would blow
// the quota fast, and IndexedDB feels overkill for single-session use.
// On refresh the list resets. Download anything you care about.
//
// The hook also owns the live recorder instance so the UI has a single
// source of truth for "am I recording right now?" and the elapsed time.
// Only one recording can be active at a time.

import { useCallback, useEffect, useRef, useState } from 'react';
import { createRecorder, type Recorder, type RecorderState } from '../recording/recorder';

export type Take = {
  id: string;
  name: string;
  blob: Blob;
  mimeType: string;
  /** Final duration in ms — filled on completion. */
  durationMs: number;
  /** Wall-clock creation time (ms epoch). Stable sort key. */
  createdAt: number;
};

export type UseRecordingsResult = {
  /** Chronological newest-first list of takes in this session. */
  takes: Take[];
  /** 'idle' when no recording in progress. */
  recorderState: RecorderState;
  /** Live elapsed-time readout for the active recording. 0 when idle. */
  currentDurationMs: number;
  start: (stream: MediaStream) => void;
  pause: () => void;
  resume: () => void;
  stop: () => void;
  /** Discard the in-flight recording without adding a take. */
  cancel: () => void;
  renameTake: (id: string, name: string) => void;
  removeTake: (id: string) => void;
  clearAll: () => void;
};

export function useRecordings(): UseRecordingsResult {
  const [takes, setTakes] = useState<Take[]>([]);
  const [recorderState, setRecorderState] = useState<RecorderState>('idle');
  const [currentDurationMs, setCurrentDurationMs] = useState(0);
  const recorderRef = useRef<Recorder | null>(null);
  // Per-session "Take N" counter — bumps on each completion, resets
  // back to 1 when clearAll() wipes the list. A ref rather than state
  // because reads happen inside the recorder's onEvent closure; state
  // would give us the stale value from the initial render.
  const nextTakeNumRef = useRef(1);

  // Tear down any active recorder on unmount.
  useEffect(() => {
    return () => {
      if (recorderRef.current?.state() === 'recording' || recorderRef.current?.state() === 'paused') {
        recorderRef.current.stop();
      }
    };
  }, []);

  const start = useCallback((stream: MediaStream) => {
    if (recorderRef.current && recorderRef.current.state() !== 'stopped' && recorderRef.current.state() !== 'idle') {
      return;
    }
    const rec = createRecorder(stream);
    recorderRef.current = rec;
    rec.onEvent((e) => {
      if (e.type === 'state') {
        setRecorderState(e.state);
        // 'stopped' reached via either stop() or cancel(); in both
        // cases the recorder instance is terminal, so clear the ref
        // and reset the elapsed-time readout. The 'complete' event
        // (if any) will push a take into the list.
        if (e.state === 'stopped') {
          recorderRef.current = null;
          setCurrentDurationMs(0);
        }
      } else if (e.type === 'duration') {
        setCurrentDurationMs(e.ms);
      } else if (e.type === 'complete') {
        const id = newTakeId();
        const take: Take = {
          id,
          name: `Take ${nextTakeNumRef.current++}`,
          blob: e.blob,
          mimeType: e.mimeType,
          durationMs: e.durationMs,
          createdAt: Date.now(),
        };
        setTakes((prev) => [take, ...prev]);
      } else if (e.type === 'error') {
        // Surface the error through state so the UI can show it; simplest
        // behaviour is to mark as idle and let the user retry.
        setRecorderState('idle');
        setCurrentDurationMs(0);
        recorderRef.current = null;
      }
    });
    rec.start();
  }, []);

  const pause = useCallback(() => {
    recorderRef.current?.pause();
  }, []);

  const resume = useCallback(() => {
    recorderRef.current?.resume();
  }, []);

  const stop = useCallback(() => {
    recorderRef.current?.stop();
  }, []);

  const cancel = useCallback(() => {
    recorderRef.current?.cancel();
  }, []);

  const renameTake = useCallback((id: string, name: string) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setTakes((prev) => prev.map((t) => (t.id === id ? { ...t, name: trimmed } : t)));
  }, []);

  const removeTake = useCallback((id: string) => {
    setTakes((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const clearAll = useCallback(() => {
    setTakes([]);
    nextTakeNumRef.current = 1;
  }, []);

  return {
    takes,
    recorderState,
    currentDurationMs,
    start,
    pause,
    resume,
    stop,
    cancel,
    renameTake,
    removeTake,
    clearAll,
  };
}

function newTakeId(): string {
  return `take_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}
